import { Button } from '@/components/ui/button';
import { AlertTriangle, Home, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function ErrorFallback({
  error,
  resetError,
}: {
  error?: Error;
  resetError?: () => void;
}) {
  const { t } = useTranslation('common');

  return (
    <div className='min-h-screen flex items-center justify-center bg-background p-4'>
      <div className='text-center space-y-6 max-w-md'>
        {/* Icon */}
        <div className='flex justify-center'>
          <div className='rounded-full bg-destructive/10 p-4'>
            <AlertTriangle className='w-16 h-16 text-destructive' />
          </div>
        </div>

        {/* Title */}
        <div className='space-y-2'>
          <h1 className='text-3xl font-bold tracking-tight'>
            {t('errorFallback.title', 'Algo salió mal')}
          </h1>
          <p className='text-muted-foreground text-lg'>
            {t(
              'errorFallback.message',
              'Lo sentimos, ha ocurrido un error inesperado.'
            )}
          </p>
        </div>

        {/* Error details (only in development) */}
        {import.meta.env.DEV && error && (
          <div className='bg-muted p-4 rounded-lg text-left'>
            <p className='font-mono text-sm text-destructive break-all'>
              {error.toString()}
            </p>
          </div>
        )}

        {/* Information */}
        <p className='text-sm text-muted-foreground'>
          {t(
            'errorFallback.reported',
            'Nuestro equipo ha sido notificado y trabajamos para solucionarlo.'
          )}
        </p>

        {/* Actions */}
        <div className='flex flex-col sm:flex-row gap-3 justify-center'>
          {resetError && (
            <Button onClick={resetError} size='lg' className='gap-2'>
              <RotateCcw className='w-4 h-4' />
              {t('errorFallback.retry', 'Reintentar')}
            </Button>
          )}
          <Button
            variant='outline'
            size='lg'
            onClick={() => (window.location.href = '/')}
            className='gap-2'
          >
            <Home className='w-4 h-4' />
            {t('errorFallback.home', 'Volver al inicio')}
          </Button>
        </div>

        {/* Support link */}
        <p className='text-xs text-muted-foreground'>
          {t('errorFallback.support', '¿Necesitas ayuda?')}{' '}
          <a
            href='mailto:infotrackv16@gmail.com'
            className='underline hover:text-foreground'
          >
            {t('errorFallback.contact', 'Contacta con soporte')}
          </a>
        </p>
      </div>
    </div>
  );
}
